import React, { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import OrderTicketModal from '../components/OrderTicketModal';
import LongTermResultsTable from '../components/LongTermResultsTable';
import LongTermHelpModal from '../components/LongTermHelpModal';
import StockChartModal from '../components/StockChartModal';
import { STOCK_INDICES, getAllIndices } from '../constants/stockIndices';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const LongTermModelPage = () => {
  const [searchParams] = useSearchParams();
  const isMobileEmbed = searchParams.get('tv_embed') === '1';

  const [selectedIndex, setSelectedIndex] = useState('NIFTY 50');
  const [periodYears, setPeriodYears] = useState(1);
  
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  
  const [selectedStockSymbol, setSelectedStockSymbol] = useState('');
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  
  // Chart modal states
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [selectedSymbolForChart, setSelectedSymbolForChart] = useState(null);
  
  // CSV Upload states
  const [stockSource, setStockSource] = useState('index'); // 'index' or 'csv'
  const [csvStocks, setCsvStocks] = useState([]);
  const [csvFileName, setCsvFileName] = useState('');
  const fileInputRef = useRef(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          if (!results.data || results.data.length === 0) {
            setError('CSV file is empty');
            return;
          }

          const firstRow = results.data[0];
          if (!firstRow.Symbol && !firstRow.symbol) {
            setError('CSV must have a "Symbol" column');
            return;
          }

          const symbols = results.data
            .map(row => row.Symbol || row.symbol)
            .filter(symbol => symbol && symbol.trim() !== '');

          if (symbols.length === 0) {
            setError('No valid symbols found in CSV');
            return;
          }

          const normalizedSymbols = symbols.map(symbol => {
            if (symbol.endsWith('.NSE') || symbol.endsWith('.NS')) {
              return symbol;
            }
            return symbol + '.NSE';
          });

          setCsvStocks(normalizedSymbols);
          setStockSource('csv');
          setCsvFileName(file.name);
          setError(null);
          
          console.log(`✅ Loaded ${normalizedSymbols.length} stocks from CSV:`, normalizedSymbols);
        } catch (err) {
          setError(`Failed to parse CSV: ${err.message}`);
        }
      },
      error: (err) => {
        setError(`CSV parsing error: ${err.message}`);
      }
    });

    event.target.value = '';
  };

  const handleDownloadResults = () => {
    if (!results || results.length === 0) {
      alert('No results to download');
      return;
    }

    try {
      const worksheet = XLSX.utils.json_to_sheet(results);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Long-Term Analysis');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
      });
      
      const timestamp = new Date().toISOString().split('T')[0];
      saveAs(data, `TrendVector_Long_Term_Scan_${timestamp}.xlsx`);
      
      console.log('✅ Results downloaded successfully');
    } catch (err) {
      console.error('Download error:', err);
      setError(`Failed to download results: ${err.message}`);
    }
  };

  const handleRunScan = async () => {
    setLoading(true);
    setError(null);
    setResults(null);
    setProgress(0);
    setProgressMessage('Initializing analysis...');
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Please log in to run the analysis');
      }
      
      const tickersToAnalyze = stockSource === 'csv' 
        ? csvStocks 
        : STOCK_INDICES[selectedIndex];
      
      if (!tickersToAnalyze || tickersToAnalyze.length === 0) {
        throw new Error('No stocks selected for analysis');
      }
      
      const requestData = {
        tickers: tickersToAnalyze,
        period_years: periodYears
      };
      
      console.log('🚀 Starting Long-Term analysis...');
      console.log('Request:', requestData);
      
      // Step 1: Start the analysis job
      const response = await fetch(`${API_BASE_URL}/api/v1/models/start-long-term-scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start analysis');
      }
      
      const { job_id } = await response.json();
      console.log('Analysis job started:', job_id);
      
      // Step 2: Connect to WebSocket for progress updates
      const wsProtocol = API_BASE_URL.startsWith('https') ? 'wss' : 'ws';
      const wsBaseUrl = API_BASE_URL.replace('https://', '').replace('http://', '');
      const wsUrl = `${wsProtocol}://${wsBaseUrl}/api/v1/models/ws/long-term-status/${job_id}`;
      
      console.log('Connecting to WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('✅ WebSocket connection established');
        setProgressMessage('Connected. Waiting for updates...');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);
          
          if (data.status === 'progress') {
            setProgress(data.progress || 0);
            setProgressMessage(data.message || 'Processing...');
          } else if (data.status === 'complete') {
            setProgress(1.0);
            setProgressMessage(data.message || 'Analysis complete!');
            setResults(data.results || []);
            setLoading(false);
            ws.close();
          } else if (data.status === 'error') {
            throw new Error(data.message || data.error || 'Analysis failed');
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
          setError(err.message);
          setLoading(false);
          ws.close();
        }
      };
      
      ws.onclose = () => {
        console.log('WebSocket connection closed');
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('Connection error. Please try again.');
        setLoading(false);
      };
      
    } catch (err) {
      setError(err.message);
      console.error('Analysis error:', err);
      setLoading(false);
    }
  };

  const handleTradeClick = (symbol) => {
    setSelectedStockSymbol(symbol);
    setIsOrderModalOpen(true);
  };

  const handleOrderSuccess = (orderData) => {
    console.log('✅ Order placed successfully:', orderData);
  };

  const handleModalClose = () => {
    setIsOrderModalOpen(false);
    setSelectedStockSymbol('');
  };

  return (
    <div className="min-h-screen flex flex-col">
      {!isMobileEmbed && <Navbar />}

      <main className="flex-1 bg-gray-50">
        <div className={`max-w-7xl mx-auto px-4 lg:px-8 ${isMobileEmbed ? 'py-3' : 'py-8'}`}>
          {!isMobileEmbed && (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
                <h1 className="text-2xl font-bold text-white">Long-Term Investment Model</h1>
                <p className="text-purple-100 mt-1">Identify stocks recovering from downtrends with bullish reversal patterns</p>
              </div>
            </div>
          )}

          {/* Filter Controls */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            {/* Header with How to Use button */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">📊 Analysis Parameters</h2>
              <button
                onClick={() => setIsHelpModalOpen(true)}
                className="flex items-center px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                How to use
              </button>
            </div>
            
            {/* Stock Source Selection */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex flex-wrap items-center gap-4">
                {/* Source Toggle Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setStockSource('index')}
                    className={`px-4 py-2 rounded-md font-medium transition-colors ${
                      stockSource === 'index'
                        ? 'bg-purple-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    📊 Select Index
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`px-4 py-2 rounded-md font-medium transition-colors ${
                      stockSource === 'csv'
                        ? 'bg-purple-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    📤 Upload CSV
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>

                {/* Index Selector (visible when stockSource === 'index') */}
                {stockSource === 'index' && (
                  <div className="flex-1 min-w-[200px]">
                    <select
                      value={selectedIndex}
                      onChange={(e) => setSelectedIndex(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      {getAllIndices().map(index => (
                        <option key={index} value={index}>{index}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Stock Count Display */}
                <div className="text-sm font-medium text-gray-700">
                  {stockSource === 'index' ? (
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full">
                      {STOCK_INDICES[selectedIndex].length} stocks selected
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full">
                      📄 {csvFileName} ({csvStocks.length} stocks)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Period Years Slider */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Analysis Period (Years): {periodYears}
              </label>
              <input
                type="range"
                min="1"
                max="5"
                step="1"
                value={periodYears}
                onChange={(e) => setPeriodYears(Number(e.target.value))}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Historical period to analyze for bullish reversal patterns
              </p>
            </div>

            {/* Run Scan Button */}
            <div className="mt-6">
              <button
                onClick={handleRunScan}
                disabled={loading}
                className={`w-full md:w-auto px-8 py-3 rounded-md font-semibold text-white transition-colors ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Running Analysis...
                  </span>
                ) : (
                  '🚀 Run Long-Term Scan'
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-red-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-red-800 font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* Real-time Progress Section */}
          {loading && (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <div className="flex items-center mb-4">
                <svg className="animate-spin h-5 w-5 text-purple-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <h3 className="font-semibold text-lg text-gray-800">Analysis in Progress...</h3>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-4 mb-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 h-4 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress * 100}%` }}
                ></div>
              </div>
              
              {/* Progress Message */}
              <div className="flex items-center justify-between text-sm">
                <p className="text-gray-700">{progressMessage}</p>
                <p className="text-gray-600 font-medium">{Math.round(progress * 100)}%</p>
              </div>
            </div>
          )}

          {/* Results Section */}
          {results && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold text-gray-800">
                    🟣 Investment Opportunities Found
                  </h2>
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                    {results.length} {results.length === 1 ? 'stock' : 'stocks'} found
                  </span>
                </div>
                
                {/* Download Button */}
                <button
                  onClick={handleDownloadResults}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Results
                </button>
              </div>

              <LongTermResultsTable 
                results={results} 
                onTradeClick={handleTradeClick}
                onChartClick={(symbol) => {
                  setSelectedSymbolForChart(symbol);
                  setIsChartModalOpen(true);
                }}
              />
            </div>
          )}
        </div>
      </main>

      {!isMobileEmbed && <Footer />}

      {/* Modals */}
      <OrderTicketModal
        isOpen={isOrderModalOpen}
        onClose={handleModalClose}
        stockSymbol={selectedStockSymbol}
        onSuccess={handleOrderSuccess}
      />

      <LongTermHelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />

      {/* Stock Chart Modal */}
      <StockChartModal
        isOpen={isChartModalOpen}
        onClose={() => setIsChartModalOpen(false)}
        symbol={selectedSymbolForChart}
      />
    </div>
  );
};

export default LongTermModelPage;
